import styled, { css, keyframes } from "styled-components"
import { Layers } from "styled-icons/feather"
import { Headline1 } from "components/common/styles"
import { Text, Flex, Box } from "rebass/styled-components"
import { StyledInput } from "components/common/form/styles"
import { theme, colors } from "theme"
import { motion } from "framer-motion"

const PICTURE_PICKER_TILE_HEIGHT = "144px"
const PICTURE_PICKER_DROP_ZONE_BLEED_PX = 16

// Post status badge styling configuration
export const STATUS_BADGE_CONFIG = {
  draft: { label: "Draft", background: colors.grey01, color: colors.text02 },
  scheduled: { label: "Scheduled", background: colors.lightYellow, color: "#b9781a" },
  partially_published: { label: "Partially published", background: colors.lightYellow, color: "#b9781a" },
  published: { label: "Published", background: colors.greenSubtle, color: colors.green },
} as const


export const FactoryPageContainer = styled.div`
  background: ${props => props.theme.colors.background1};
  min-height: 100vh;
`;

export const FactoryContentWrapper = styled.div`
   padding: 30px 36px 80px;
   max-width: 1080px;
   margin: 0 auto;
`;

export const FactoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 8px;
`;

export const FactoryPageTitle = styled.h1`
  font-family: ${p => p.theme.fonts.heading};
  font-size: 24px;
  font-weight: 700;
  color: ${p => p.theme.colors.darkBlack};
  margin-bottom: 24px;
`;

export const Page = styled(Flex)`
  min-height: calc(100vh - 64px);
  padding: 24px;
  background: ${p => p.theme.colors.white};
  display: grid;
  grid-template-rows: 1fr auto 1fr;
`

export const HeaderArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  text-align: center;
`

export const CenterArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

export const SpacerArea = styled.div``

export const CenterStack = styled.div`
  position: relative;
  width: 100%;
  max-width: 520px;
`

export const FloatingSpinner = styled.div`
  position: absolute;
  top: -80px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
`

export const FloatingMessage = styled(motion.div)`
  position: absolute;
  top: calc(100% + 20px);
  left: 0;
  right: 0;
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`

// In-flow variant: the failure block lives inside cards whose body is an
// overflow:hidden accordion, which clips the absolutely-positioned FloatingMessage.
export const FailureMessage = styled(motion.div)`
  margin-top: 20px;
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`

export const Content = styled(Flex)`
  width: 100%;
  max-width: 900px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`

export const Title = styled(Headline1)`
  line-height: 34px;
  letter-spacing: -0.03em;
  color: ${p => p.theme.colors.darkBlack};
`

export const Subtitle = styled(Text)`
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: -0.16px;
  color: ${p => p.theme.colors.text02};
  max-width: 580px;
`

const twinkle = keyframes`
  0%, 100% { opacity: 0.45; }
  50% { opacity: 0.8; }
`

export const PageBranding = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`

export const BrandingTitle = styled.h1`
  font-family: ${p => p.theme.fonts.heading};
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: ${p => p.theme.colors.blue};
  margin: 0;
`

export const SparkleIcon = styled.span`
  display: inline-flex;
  color: ${p => p.theme.colors.blue};
  animation: ${twinkle} 3s ease-in-out infinite;
  margin-bottom: 6px;
`

type InputSize = "large" | "compact";

export const TextInputContainer = styled.div<{ $inputSize?: InputSize }>`
  position: relative;
  width: 100%;
  max-width: ${p => p.$inputSize === "compact" ? "520px" : "720px"};
  height: ${p => p.$inputSize === "compact" ? "48px" : "64px"};

  background: ${p => p.theme.colors.white};
  border: 1px solid #66778029;
  box-shadow: ${theme.shadows.shadowS};
  border-radius: ${p => p.$inputSize === "compact" ? "10px" : "12px"};

  display: flex;
  align-items: center;

  &:focus-within {
    border-color: ${p => p.theme.colors.blue};
  }
`

export const TextInput = styled(StyledInput)<{ $inputSize?: InputSize }>`
  width: 100%;
  height: 100%;
  margin-top: 0;
  border: none;
  background: transparent;
  padding-right: ${p => p.$inputSize === "compact" ? "220px" : "170px"};
  font-size: ${p => p.$inputSize === "compact" ? "14px" : "16px"};

  &:focus {
    outline: none;
    box-shadow: none;
  }
`

export const SearchOptions = styled(Box)`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);

  display: flex;
  align-items: center;
`

export const StatusCard = styled.div`
  width: 100%;
  max-width: 520px;
  background: ${p => p.theme?.colors?.white ?? "#fff"};
  border: 1px solid rgba(96, 110, 128, 0.12);
  border-radius: 12px;
  padding: 18px 20px;
  box-shadow: 0px 1px 2px rgba(28, 30, 31, 0.04), 0px 12px 32px rgba(28, 30, 31, 0.08);
  text-align: left;
`

export const StepRow = styled.div`
  display: flex;
  gap: 14px;

  &:not(:last-child) {
    padding-bottom: 18px;
  }
`

export const StepLeft = styled.div`
  width: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
`

export const StepConnector = styled.div`
  margin-top: 10px;
  width: 2px;
  min-height: 24px;
  flex: 1;
  border-radius: 2px;
  background: rgba(39, 174, 96, 0.55);
`

export const StepBody = styled.div`
  flex: 1;
  padding-top: 2px;
`

export const StepTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: -0.005em;
  color: #1c1e1f;
`

export const StepSubtitle = styled.div`
  margin-top: 6px;
  font-size: 14px;
  font-weight: 400;
  line-height: 14px;
  letter-spacing: -0.005em;
  color: #808082;
`

export const StepIconDone = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: rgba(39, 174, 96, 1);
  color: ${p => p.theme?.colors?.white ?? "#fff"};
  display: grid;
  place-items: center;
  font-size: 14px;
  font-weight: 700;
  box-shadow: 0px 6px 16px rgba(39, 174, 96, 0.25);
`

export const StepIconActive = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: ${p => p.theme?.colors?.white ?? "#fff"};
  border: 2px solid rgba(47, 128, 237, 1);
  color: rgba(47, 128, 237, 1);
  display: grid;
  place-items: center;
  font-size: 14px;
  font-weight: 700;
`

export const StepIconInactive = styled(StepIconActive)`
  border-color: rgba(96, 110, 128, 0.24);
  color: rgba(96, 110, 128, 0.45);
`

export const StepConnectorMuted = styled(StepConnector)`
  background: rgba(96, 110, 128, 0.16);
`

export const UpdatePill = styled.div`
  margin-top: 10px;
  width: 100%;
  max-width: 340px;
  border-radius: 6px;
  background: rgba(47, 128, 237, 0.08);
  padding: 8px 10px;
`

export const UpdateText = styled.div`
  font-size: 14px;
  line-height: 18px;
  letter-spacing: -0.005em;
  color: rgba(47, 128, 237, 1);
`

export const ErrorText = styled.div`
  margin-top: 10px;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: -0.005em;
  color: rgba(235, 87, 87, 1);
`

export const Muted = styled.div`
  opacity: 0.45;
`

export const StepIconError = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: rgba(235, 87, 87, 1);
  color: ${p => p.theme?.colors?.white ?? "#fff"};
  display: grid;
  place-items: center;
  font-size: 14px;
  font-weight: 700;
  box-shadow: 0px 6px 16px rgba(235, 87, 87, 0.25);
`

export const StepConnectorError = styled(StepConnector)`
  background: rgba(235, 87, 87, 0.55);
`

export const MotionMessage = styled(motion.div)`
  margin-top: 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const InlineTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: -0.005em;
  color: ${p => p.theme.colors.darkBlack};
`

export const InlineSubtitle = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: -0.005em;
  color: ${p => p.theme.colors.text02};
`

export const ProfileReadyDialog = styled.div`
  width: 100%;
  max-width: 520px;
  background: ${p => p.theme?.colors?.white ?? "#fff"};
  border: 1px solid rgba(96, 110, 128, 0.12);
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
`

export const ProfileReadyActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`

export const LeadIcon = styled(Layers)`
  flex: none;
  color: ${p => p.theme.colors.blue};
`

// -- Tab Navigation Styles --

export const TabsContainer = styled(Flex)`
  box-sizing: border-box;
  gap: 6px;

  * {
    box-sizing: border-box;
  }
`;

export const TabButton = styled.button<{ isActive: boolean }>`
  appearance: none;
  background: ${props =>
    props.isActive
      ? props.theme.colors.black
      : props.theme.colors.grey02};
  border: 0;
  border-radius: 999px;
  box-shadow: none;
  color: ${props =>
    props.isActive ? props.theme.colors.white : props.theme.colors.black};
  cursor: pointer;
  font-family: ${props => props.theme.fonts.body};
  font-size: 14.5px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.08px;
  overflow: hidden;
  outline: none;
  padding: 9px 22px;
  position: relative;
  transition: all 0.2s ease-in-out;

  &:hover,
  &:focus {
    color: ${props =>
    props.isActive
      ? props.theme.colors.white
      : props.theme.colors.black};
  }
`;

// -- ProfileCard Styles --

export const CardContainer = styled.div`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.grey02};
  border-radius: 12px;
  box-shadow: ${p => p.theme.shadows.shadowS};
  overflow: hidden;
  transition: all 0.2s ease-in-out;
  width: 100%;

  &:hover {
    box-shadow: ${p => p.theme.shadows.shadowM};
  }
`;

export const CardHeader = styled(Flex)`
  padding: 18px;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  background: ${p => p.theme.colors.white};
  border-bottom: 1px solid transparent;

  &[data-expanded="true"] {
     border-bottom: 1px solid ${p => p.theme.colors.grey02};
  }
`;

// -- ProfileCard header (new design) --

// $plain drops the tinted background so a real logo sits on the card itself;
// the letter fallback keeps the primarySubtle tile.
export const SrcTile = styled.div<{ $plain?: boolean }>`
  width: 42px;
  height: 42px;
  flex: none;
  border-radius: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${p => (p.$plain ? "transparent" : p.theme.colors.primarySubtle)};
  color: ${p => p.theme.colors.blue};
  overflow: hidden;
`;

export const SrcTileLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 7px;
`;

// Fallback when there's no light-theme logo: the profile-name initial. Weight 500
// keeps the stroke close to the Feather globe it replaces; color/shape come from SrcTile.
export const SrcTileLetter = styled.span`
  font-size: 20px;
  font-weight: 500;
  line-height: 1;
  text-transform: uppercase;
`;

export const CardHeaderTitle = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ProfileName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15.5px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${p => p.theme.colors.darkBlack};
`;

export const SourceTag = styled.span`
  flex: none;
  font-size: 11.5px;
  font-weight: 600;
  color: ${p => p.theme.colors.text03};
  background: ${p => p.theme.colors.grey01};
  padding: 2px 8px;
  border-radius: 999px;
`;

export const ProfileSubtitle = styled.div`
  margin-top: 3px;
  font-size: 12.5px;
  color: ${p => p.theme.colors.text03};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CardHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: none;
`;

const ChipBase = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  padding: 4px 11px;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
`;

export const ThemeChip = styled(ChipBase)`
  background: ${p => p.theme.colors.primarySubtle};
  color: ${p => p.theme.colors.blue};
`;

export const StatusChip = styled(ChipBase)<{ $tone?: "success" | "error" | "pending" }>`
  background: ${p =>
    p.$tone === "error" ? "#fdecec" : p.$tone === "pending" ? colors.lightYellow : p.theme.colors.greenSubtle};
  color: ${p =>
    p.$tone === "error" ? p.theme.colors.red : p.$tone === "pending" ? "#b9781a" : p.theme.colors.green};
`;

export const CountChip = styled(ChipBase)`
  background: ${p => p.theme.colors.primarySubtle};
  color: ${p => p.theme.colors.blue};
`;

export const HeaderIconButton = styled.button<{ $danger?: boolean }>`
  width: 34px;
  height: 34px;
  flex: none;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: ${p => p.theme.colors.text02};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.14s ease, color 0.14s ease;

  &:hover {
    background: ${p => p.theme.colors.grey01};
    color: ${p => p.theme.colors.darkBlack};
  }

  ${p =>
    p.$danger &&
    css`
      &:hover {
        background: #fdecec;
        color: ${p.theme.colors.red};
      }
    `}
`;

export const CardBody = styled.div<{ isOpen: boolean }>`
  display: grid;
  grid-template-rows: ${p => (p.isOpen ? "1fr" : "0fr")};
  transition: grid-template-rows 0.3s ease-out;
`;

export const CardContent = styled.div`
  min-height: 0;
  overflow: hidden;
`;

export const CardInner = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
`;

// Same 1px rule as the FieldsGrid row dividers — used between the profile card's sections.
export const RowDivider = styled.div`
  border-top: 1px solid ${p => p.theme.colors.grey02};
`;


export const HeaderInfo = styled(Flex)`
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

export const SectionLabel = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.theme.colors.darkBlack};
  margin-bottom: 8px;
`;

export const ProfileTextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box; /* Fixes overflow with padding */
  min-height: 360px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.grey02};
  resize: vertical;
  color: ${p => p.theme.colors.darkBlack};
  font-size: 14px;
  line-height: 1.5;
  background: ${p => p.theme.colors.grey01};

  &:focus {
      outline: none;
      border-color: ${p => p.theme.colors.blue};
  }
`;

// Inline pill switcher (Preview / Edit) — the active option is a white pill
// with a soft shadow sliding within a light track.
export const SegMini = styled.div`
  display: inline-flex;
  align-self: flex-start;
  gap: 4px;
  background: ${p => p.theme.colors.grey01};
  padding: 3px;
  border-radius: 999px;
  margin-bottom: 12px;
`

// Borderless wrapper for the profile-text section (no card chrome — the editor
// box provides its own border).
export const ProfileSection = styled.div`
  display: flex;
  flex-direction: column;
`

export const CollapsibleHeading = styled.h3`
  margin: 0;
`

export const CollapsibleHeader = styled.button`
  appearance: none;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px 0;
  border: 0;
  background: transparent;
  font-family: ${p => p.theme.fonts.body};
  text-align: left;
  cursor: pointer;
`

export const CollapsibleTitles = styled.div`
  flex: 1;
  min-width: 0;
`

export const CollapsibleTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${p => p.theme.colors.darkBlack};
`

export const CollapsibleDescription = styled.div`
  margin-top: 2px;
  font-size: 13px;
  color: ${p => p.theme.colors.text03};
`

// Same grid-rows accordion as CardBody. Visibility flips only after the close animation, which
// keeps the collapsed controls out of the tab order without cutting the animation short.
export const CollapsibleBody = styled.div<{ $isOpen: boolean }>`
  display: grid;
  grid-template-rows: ${p => (p.$isOpen ? "1fr" : "0fr")};
  visibility: ${p => (p.$isOpen ? "visible" : "hidden")};
  transition: grid-template-rows 0.3s ease-out, visibility 0.3s;
`

export const CollapsibleClip = styled.div`
  min-height: 0;
  overflow: hidden;
`

export const CollapsibleContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 20px;
`

// Theme section header: title + delete sitting right next to each other (no box).
export const ThemeSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
`

export const SegMiniButton = styled.button<{ isActive: boolean }>`
  appearance: none;
  border: 0;
  cursor: pointer;
  font-family: ${p => p.theme.fonts.body};
  font-size: 12.5px;
  font-weight: 600;
  padding: 6px 16px;
  border-radius: 999px;
  color: ${p => (p.isActive ? p.theme.colors.darkBlack : p.theme.colors.text02)};
  background: ${p => (p.isActive ? p.theme.colors.white : "transparent")};
  box-shadow: ${p => (p.isActive ? "0 1px 3px rgba(23, 25, 28, 0.12)" : "none")};
  outline: none;
`

export const MarkdownPreview = styled.div`
  width: 100%;
  box-sizing: border-box;
  height: 360px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.grey02};
  background: ${p => p.theme.colors.white};
  color: ${p => p.theme.colors.darkBlack};
  font-size: 14px;
  line-height: 1.5;
  overflow-y: auto;

  h2 { font-size: 18px; margin: 16px 0 8px; }
  h3 { font-size: 16px; margin: 12px 0 6px; }
  p { margin: 0 0 8px; }
`

// Profile preview rendered as a two-column field grid (matches the design):
// grey uppercase keys, dividers between rows, wide fields span both columns.
export const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px 28px;
`

export const Field = styled.div<{ $wide?: boolean }>`
  padding: 13px 0;
  border-top: 1px solid ${p => p.theme.colors.grey02};

  &:nth-child(1),
  &:nth-child(2) {
    border-top: 0;
  }

  ${p => p.$wide && css`grid-column: 1 / -1;`}
`

export const FieldKey = styled.div`
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${p => p.theme.colors.text03};
  margin-bottom: 5px;
`

export const FieldValue = styled.div`
  font-size: 14px;
  color: ${p => p.theme.colors.darkBlack};
  line-height: 1.55;

  & > :first-child { margin-top: 0; }
  & > :last-child { margin-bottom: 0; }
  p { margin: 0 0 8px; }
  ul, ol { margin: 0 0 8px; padding-left: 20px; }
`

export const ThemeChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
`

export const CardFooter = styled(Flex)`
  margin-top: 24px;
  align-items: center;
  justify-content: space-between;
`;

export const SavedText = styled(Text)<{ isError?: boolean }>`
    font-size: 12px;
    color: ${p => p.isError ? p.theme.colors.red : p.theme.colors.text02};
    font-style: italic;
`;

export const ExpandIcon = styled.div<{ isExpanded: boolean }>`
  display: flex;
  transform: ${p => (p.isExpanded ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.2s ease-in-out;
`;

export const FormAlignRight = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 24px;
`;

export const PostsPlaceholder = styled.div`
  width: 100%;
  min-height: 400px;
  background: ${p => p.theme.colors.grey01};
  border: 1px dashed ${p => p.theme.colors.grey02};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.colors.text02};
  font-size: 14px;
`;

export const ChannelSelectButton = styled.button`
  display: flex;
  align-items: center;
  gap: 9px;
  flex: none;
  max-width: 230px;
  border: 1px solid ${p => p.theme.colors.grey02};
  border-radius: 999px;
  padding: 5px 12px 5px 5px;
  background: ${p => p.theme.colors.white};
  cursor: pointer;
  color: ${p => p.theme.colors.text01};
  font-size: 13.5px;
  font-weight: 600;

  /* The avatar is an inline <img>; its baseline gap makes the wrapper taller than
     the image, so align-items:center leaves it sitting high. block removes the gap. */
  img {
    display: block;
  }

  &:hover {
    background: ${p => p.theme.colors.grey01};
  }
`

export const ChannelName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

// Batch header meta line ("Posts created from <source> on <date>") — single
// line; the source name is capped so a long name can't push it multi-line.
export const BatchMeta = styled.div`
  flex: 1;
  min-width: 0;
  padding: 2px 0 2px 8px; // room for the rename pill's edges, which overflow: hidden would otherwise clip
  margin: -2px 0 -2px -8px;
  font-size: 12.5px;
  color: ${p => p.theme.colors.text03};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const BatchSourceName = styled.span`
  display: inline-block;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
  font-weight: 600;
  color: ${p => p.theme.colors.darkBlack};
`

export const BatchHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
`

export const ChannelMenuItem = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  border: none;
  background: ${p => (p.$selected ? p.theme.colors.grey01 : "transparent")};
  padding: 8px 16px;
  cursor: pointer;
  color: ${p => p.theme.colors.text01};

  &:hover {
    background: ${p => p.theme.colors.grey01};
  }
`

export const ChannelMenuRow = styled.div<{ $open?: boolean }>`
  display: flex;
  flex-direction: column;
  background: ${p => (p.$open ? p.theme.colors.grey01 : "transparent")};
`

export const ChannelMenuRowTop = styled.div`
  display: flex;
  align-items: center;

  > button:first-child {
    flex: 1;
    min-width: 0;
  }
`

export const ChannelMenuName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: inherit;
`

export const CategorySummaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: none;
  border: none;
  background: transparent;
  padding: 8px 16px 8px 4px;
  font-size: 12px;
  font-weight: 400;
  color: ${p => p.theme.colors.text03};
  cursor: pointer;

  &:hover {
    color: ${p => p.theme.colors.blue};
  }

  svg {
    transition: transform 0.15s ease;
  }

  &[aria-expanded="true"] svg {
    transform: rotate(180deg);
  }
`

export const CategoryDrawer = styled.div<{ $inCard?: boolean }>`
  padding: ${p => (p.$inCard ? "4px 18px 14px" : "2px 16px 12px 52px")};
  ${p => (p.$inCard ? `border-top: 1px solid ${p.theme.colors.grey02};` : "")}
`

export const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
`

export const CategoryPill = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  border: 1px solid ${p => (p.$active ? "transparent" : p.theme.colors.grey02)};
  background: ${p => (p.$active ? p.theme.colors.primarySubtle : "transparent")};
  color: ${p => (p.$active ? p.theme.colors.blue : p.theme.colors.text02)};
  border-radius: 999px;
  padding: 5px 11px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: ${p => (p.$active ? "transparent" : p.theme.colors.grey04)};
    color: ${p => (p.$active ? p.theme.colors.blue : p.theme.colors.text01)};
  }
`

export const ChannelAvatarGroup = styled.div` // not an overlapping stack: the same logo on every network means the service badge must stay visible
  display: flex;
  align-items: center;
  gap: 5px;
  flex: none;
`

export const ChannelCountLabel = styled.span`
  font-size: 13.5px;
  font-weight: 600;
  color: ${p => p.theme.colors.text01};
  white-space: nowrap;
`

export const ChannelMenuItemMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
`

export const ChannelMenuItemType = styled.span`
  font-size: 12px;
  color: ${p => p.theme.colors.text02};
`

export const ChannelMenuCheckbox = styled.input`
  flex: none;
  accent-color: ${p => p.theme.colors.blue};
  width: 16px;
  height: 16px;
  cursor: pointer;
`

export const ChannelListModalList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0;
`

export const ChannelListModalLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.theme.colors.text01};
`

export const ChannelListModalHint = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${p => p.theme.colors.text02};
`

export const GeneratingPlaceholder = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px;
  color: ${p => p.theme.colors.text02};
  background: rgba(255, 255, 255, 0.5);
  border-radius: 8px;
  border: 1px dashed ${p => p.theme.colors.grey02};
`

export const GeneratingSpinnerRow = styled(Flex)`
  align-items: center;
  gap: 12px;
`

export const GeneratingLabel = styled.div`
  font-size: 14px;
`

export const GeneratingCountdown = styled.div`
  font-size: 13px;
  color: ${p => p.theme.colors.text02};
`

export const GenerateMoreSlot = styled(Flex)`
  justify-content: center;
  margin-top: 8px;
`

// -- Modal Styles --

export const ModalIconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin: auto;
  background: ${p => p.theme.colors.grey01};
  border-radius: 12px;

  > span {
    font-size: 18px;
  }
`

export const ModalInfoWrapper = styled(Flex)`
  flex-direction: column;
  gap: 16px;
  text-align: center;
  justify-content: center;
`

export const ModalInfoPara = styled.p`
  margin-top: 8px;
  line-height: 140%;
  font-weight: 400;
  color: ${p => p.theme.colors.text02};
`

export const ModalButtonGroup = styled(Flex)`
  flex-direction: column;
  gap: 8px;
`

export const ModalContentWrapper = styled.div`
  max-width: 272px;
  margin: auto;
`

// -- Post Row Styles --

const postRowEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

export const RowContainer = styled.div`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.grey02};
  border-radius: 8px;
  overflow: hidden;
  animation: ${postRowEnter} 0.35s ease both;

  &:not(:last-child) {
    margin-bottom: 8px;
  }
`

export const RowHeader = styled(Flex)`
  padding: 12px 16px;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  background: ${p => p.theme.colors.white};

  &[data-expanded="true"] {
    border-bottom: 1px solid ${p => p.theme.colors.grey02};
  }
`

export const RowContent = styled.div`
  padding: 16px;
  background: ${p => p.theme.colors.white};
`

// Static (non-collapsible) section header used inside an always-open row —
// just a title and its controls, no expand/collapse affordance.
export const RowSectionHeader = styled(Flex)`
  padding: 12px 16px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${p => p.theme.colors.grey02};
`

export const Badge = styled.span<{ $background: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${p => p.$background};
  color: ${p => p.$color};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`
// Both labels share one grid cell; overflow:hidden on the pill clips whichever
// is hidden. Only the pill's width is animated — between an explicit "Draft" and
// "Approve" size — so it grows/shrinks gradually and identically in both
// directions, sliding the rest of the row right. The label/icon and colour swap
// instantly. Widths are in em so they track font-size; tune to fit the labels.
export const ApprovePillDefault = styled.span`
  grid-area: 1 / 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
`
export const ApprovePillHover = styled.span`
  grid-area: 1 / 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  white-space: nowrap;
  opacity: 0;
`
export const ApprovePill = styled.button`
  display: inline-grid;
  /* Pin the column to the pill width (not the wider "Approve" max-content) so
     both labels center on the pill and clip symmetrically. */
  grid-template-columns: minmax(0, 1fr);
  justify-items: center;
  align-items: center;
  box-sizing: border-box;
  width: 4.5em;
  padding: 4px 10px;
  border: 0;
  border-radius: 999px;
  overflow: hidden;
  background: ${p => p.theme.colors.grey01};
  color: ${p => p.theme.colors.text02};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: width 0.2s ease;

  &:hover {
    width: 7.6em;
    background: ${p => p.theme.colors.greenSubtle};
    color: ${p => p.theme.colors.green};
  }

  &:hover ${ApprovePillDefault} {
    opacity: 0;
  }

  &:hover ${ApprovePillHover} {
    opacity: 1;
  }

  &:disabled {
    cursor: default;
    opacity: 0.65;
  }
`

export const PostPreviewText = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  line-height: 1.4;
  color: ${p => p.theme.colors.darkBlack};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

// Post rows are separated by dividers (no box): a top border between rows, none
// above the first row and none below the last.
export const PostRowItem = styled.div`
  border-top: 1px solid ${p => p.theme.colors.grey02};

  &:first-child {
    border-top: none;
  }
`

export const PostRowLine = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 13px 6px;
  cursor: pointer;
`

// Status + category sit together, immediately adjacent (no fixed columns).
export const PostStat = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
`

export const PostWhen = styled.div`
  flex: none;
  font-size: 12.5px;
  color: ${p => p.theme.colors.text03};
  white-space: nowrap;
`

export const SeriesSlot = styled.div`
  display: flex;
  width: 24px;
  flex: none;
`

export const SeriesBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 24px;
  height: 24px;
  border: 1px solid ${p => p.theme.colors.grey02};
  border-radius: 50%;
  color: ${p => p.theme.colors.text02};
`

export const PostRowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex: none;
`

// Same grid-rows accordion as CardBody: animate 0fr↔1fr so the detail expands
// and collapses smoothly. The clip layer (min-height:0 + overflow:hidden) lets
// the row actually shrink to zero.
export const PostRowDetailWrapper = styled.div<{ isOpen: boolean }>`
  display: grid;
  grid-template-rows: ${p => (p.isOpen ? "1fr" : "0fr")};
  transition: grid-template-rows 0.3s ease-out;
`

export const PostRowDetailClip = styled.div`
  min-height: 0;
  overflow: hidden;
`

export const PostRowDetail = styled.div`
  border-top: 1px solid ${p => p.theme.colors.grey02};
  background: ${p => p.theme.colors.background3};
  padding: 20px 16px;
`
const POST_DETAIL_SIDE = "156px"

export const PostDetailLayout = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 16px;
`

export const PostDetailSpacer = styled.div`
  flex: none;
  width: ${POST_DETAIL_SIDE};
`

export const PostPreviewSlot = styled.div`
  flex: 0 1 555px;
  min-width: 0;
`

export const SourceInfoButton = styled.button`
  width: 24px;
  height: 24px;
  flex: none;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: ${p => p.theme.colors.text02};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: help;
  transition: background 0.14s ease, color 0.14s ease;

  &:hover,
  &:focus-visible {
    background: ${p => p.theme.colors.grey01};
    color: ${p => p.theme.colors.darkBlack};
  }
`

export const PostDetailButtons = styled.div`
  flex: none;
  width: ${POST_DETAIL_SIDE};
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
`

// Edit / Approve share a base so they match in size, weight and shape; both fill the column so
// they're equal width. Border 1px so the outline stays thin.
const detailButtonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
  box-sizing: border-box;
  padding: 7px 14px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-family: Inter;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.2;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.14s ease, color 0.14s ease, border-color 0.14s ease;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`
export const EditComposerButton = styled.button`
  ${detailButtonBase}
  background: ${p => p.theme.colors.background1};
  color: ${p => p.theme.colors.darkBlack};
  border-color: ${p => p.theme.colors.grey04};

  &:hover {
    background: ${p => p.theme.colors.grey01};
  }
`
export const ApproveButton = styled.button`
  ${detailButtonBase}
  background: ${p => p.theme.colors.greenSubtle};
  color: ${p => p.theme.colors.green};
  border-color: ${p => p.theme.colors.greenSubtleDark};

  &:hover {
    background: ${p => p.theme.colors.green};
    color: #ffffff;
    border-color: ${p => p.theme.colors.green};
  }
`
export const PostStatusTag = styled.div<{ $background: string; $color: string }>`
  ${detailButtonBase}
  cursor: default;
  background: ${p => p.$background};
  color: ${p => p.$color};
`

export const DuplicateBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${p => p.theme.colors.grey01};
  color: ${p => p.theme.colors.text02};
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  cursor: help;
`

// Fixed-width column that holds the duplicate badge. The fixed width is what
// makes the D land at the same x-position across rows regardless of how long
// each row's preview text is.
export const DuplicateSlot = styled.div`
  width: 64px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
`

// Anchor styling for inline links inside the (dark) duplicate tooltip.
// Locks the link color across all states (the browser's default :visited turns
// links blue/purple, which is unreadable on dark backgrounds). Hover feedback
// is a subtle white background tint instead of a color change.
export const TooltipLink = styled.a`
  color: #ffffff;
  text-decoration: underline;
  font-weight: 600;
  border-radius: 2px;
  padding: 0 2px;
  margin: 0 -2px;
  transition: background 120ms ease;

  &,
  &:link,
  &:visited,
  &:hover,
  &:active,
  &:focus {
    color: #ffffff;
  }

  &:hover,
  &:focus-visible {
    background: rgba(255, 255, 255, 0.14);
  }

  &:active {
    background: rgba(255, 255, 255, 0.22);
  }
`

export const IconButton = styled.button<{ $variant?: "danger" }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${p => p.$variant === "danger" ? p.theme.colors.red : p.theme.colors.text02};
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 16px;

  &:hover {
    background: ${p => p.$variant === "danger" ? "rgba(235, 87, 87, 0.1)" : "rgba(96, 110, 128, 0.1)"};
    color: ${p => p.$variant === "danger" ? p.theme.colors.red : p.theme.colors.darkBlack};
  }
`

// -- LinkedIn Preview Styles --

const LINKEDIN_BLUE = "#0a66c2"
const LINKEDIN_TEXT = "#191919"
const LINKEDIN_LIGHT_TEXT = "#666666"
const LINKEDIN_BORDER = "#e0e0e0"
const LINKEDIN_HOVER_BG = "#f3f6f8"

export const LinkedInPreviewCard = styled.div`
  width: 100%;
  max-width: 555px;
  margin: 0 auto;
  background: white;
  border: 1px solid ${LINKEDIN_BORDER};
  border-radius: 8px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 4px 6px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif;
`

export const LinkedInPreviewHeader = styled.div`
  padding: 12px 16px 4px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`

export const LinkedInHeaderContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`

export const LinkedInAvatarWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
`

export const LinkedInAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

export const LinkedInHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const LinkedInUserName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${LINKEDIN_TEXT};
`

export const LinkedInUserFollowers = styled.span`
  font-size: 12px;
  color: ${LINKEDIN_LIGHT_TEXT};
`

export const LinkedInPostMeta = styled.span`
  font-size: 12px;
  color: ${LINKEDIN_LIGHT_TEXT};
  display: flex;
  align-items: center;
  gap: 4px;
`

export const LinkedInPreviewBody = styled.div`
  padding: 4px 16px 8px;
`

export const LinkedInPostContent = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: ${LINKEDIN_TEXT};
  white-space: pre-line;
  margin-bottom: 12px;
`

// Inline "more"/"less" toggle for long post text — muted grey like LinkedIn's "see more".
export const LinkedInMoreLink = styled.span`
  color: ${p => p.theme.colors.text02};
  cursor: pointer;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`

export const LinkedInHighlight = styled.span`
  color: ${LINKEDIN_BLUE};
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

export const LinkedInLink = styled.a`
  color: ${LINKEDIN_BLUE};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

export const LinkedInPostImageWrapper = styled.div`
  /* 32px cancels the 16px body padding on each side; +2px covers the card's 1px border on each side.
     overflow: hidden on LinkedInPreviewCard clips any overshoot. */
  position: relative; /* anchors the regenerate pill + the regenerating overlay */
  width: calc(100% + 34px);
  margin-left: -17px;
  margin-bottom: 12px;
`

export const LinkedInPostImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`

// Wraps the hover-revealed visual pills (Regenerate, Change picture) in the top corner of a
// post's visual. Hidden until the image wrapper is hovered (or a pill inside is focused), and
// sits above the (pointer-events:none) preview iframe so the pills stay clickable.
export const VisualHoverActions = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
  display: flex;
  gap: 8px;
  opacity: 0;
  transform: translateY(-2px);
  pointer-events: none;
  transition: opacity 0.15s ease, transform 0.15s ease;

  ${LinkedInPostImageWrapper}:hover &,
  &:focus-within {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }
`

const visualPillBase = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: none;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
`

export const RegenerateVisualButton = styled.button`
  ${visualPillBase}
  background: rgba(15, 23, 42, 0.72);
  color: #fff;

  &:hover {
    background: rgba(15, 23, 42, 0.9);
  }
`

export const ChangePictureButton = styled.button`
  ${visualPillBase}
  background: rgba(15, 23, 42, 0.72);
  color: #fff;

  &:hover {
    background: rgba(15, 23, 42, 0.9);
  }
`

// Loading state over the visual while a new one is generated: the current image is blurred
// behind a frosted scrim with a spinner-and-label badge on top.
export const RegeneratingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
`

// Solid dark pill (same look as RegenerateVisualButton) so the spinner and label stay
// readable no matter how dark or light the visual behind the scrim is.
export const RegeneratingBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.72);
`

export const RegeneratingOverlayLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #fff;
`

// Picture picker modal — a single scrolling row of thumbnails at the rendered visual's own
// aspect ratio (4:5, matching the fixed 1080x1350 canvas), sized so a whole number of them
// sit fully inside the modal at rest instead of cutting one off at the edge.
export const PicturePickerFilmstrip = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 4px;
  scroll-snap-type: x mandatory;
`

export const PicturePickerThumb = styled.button<{ $selected?: boolean; $disabled?: boolean }>`
  position: relative;
  flex: none;
  width: 120px;
  height: ${PICTURE_PICKER_TILE_HEIGHT};
  border-radius: 8px;
  overflow: hidden;
  padding: 0;
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(p) => (p.$disabled ? 0.45 : 1)};
  background-size: cover;
  background-position: center;
  scroll-snap-align: start;
  border: 2px solid ${(p) => (p.$selected ? theme.colors.primary : "transparent")};
  box-shadow: ${(p) => (p.$selected ? "0 0 0 2px rgba(0, 99, 251, 0.18)" : "none")};
`

export const PicturePickerUnusable = styled.span`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 3px 4px;
  background: rgba(15, 23, 42, 0.78);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  text-align: center;
`

export const PicturePickerCheck = styled.span`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${theme.colors.primary};
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  display: grid;
  place-items: center;
`

export const PicturePickerEmpty = styled.p`
  margin: 0;
  align-self: center;
  font-size: 14px;
  color: ${theme.colors.text02};
`

export const PicturePickerLoadFailed = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  font-size: 14px;
  color: ${theme.colors.text02};
`

// Bleeds 16px into the modal's 24px padding so a drop just off the content still lands here, while
// the dashed border keeps 8px from the modal edge. The padding gives the border's 1px back so the
// content sits where it would without the zone.
export const PicturePickerDropZone = styled(ModalInfoWrapper)<{ $isDragging: boolean }>`
  margin: -${PICTURE_PICKER_DROP_ZONE_BLEED_PX}px;
  padding: ${PICTURE_PICKER_DROP_ZONE_BLEED_PX - 1}px;
  border: 1px dashed ${(p) => (p.$isDragging ? p.theme.colors.blue : "transparent")};
  border-radius: 12px;
  background: ${(p) => (p.$isDragging ? `${p.theme.colors.blue}08` : "transparent")};
  transition: border-color 120ms ease, background 120ms ease;
`

export const LinkedInEmptyContent = styled.div`
  font-size: 14px;
  color: ${LINKEDIN_LIGHT_TEXT};
  font-style: italic;
  text-align: center;
  padding: 24px 0;
`

// Single action-bar line: avatar + caret, action items (with counts), reactions on the right.
export const LinkedInActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: ${LINKEDIN_LIGHT_TEXT};
`

export const LinkedInActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
`

export const LinkedInActionAvatar = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`

export const LinkedInActionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  color: ${LINKEDIN_LIGHT_TEXT};
`

export const LinkedInLikeIconBadge = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${LINKEDIN_BLUE};
  display: flex;
  align-items: center;
  justify-content: center;
`

export const LinkedInSmallAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
`

// -- Add Source Modal Styles --

export const SourceModalContainer = styled.div`
  position: relative;
`

export const SourceModalCloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${p => p.theme.colors.text02};
  cursor: pointer;
  z-index: 1;

  &:hover {
    background: ${p => p.theme.colors.grey01};
    color: ${p => p.theme.colors.darkBlack};
  }
`

export const SourceModalHeader = styled.div`
  padding: 28px 40px 16px;
`

export const SourceModalTitle = styled.h2`
  font-family: ${p => p.theme.fonts.heading};
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: ${p => p.theme.colors.darkBlack};
  margin: 0 0 6px;
`

export const SourceModalSubtitle = styled.p`
  font-size: 14px;
  line-height: 1.5;
  color: ${p => p.theme.colors.text02};
  margin: 0;
`

export const SourceSectionsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 40px 20px;
`

export const SourceCard = styled.div<{ $disabled?: boolean }>`
  padding: 16px;
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.grey02};
  border-radius: 12px;
  transition: all 0.25s ease;
  opacity: ${p => p.$disabled ? 0.4 : 1};
  pointer-events: ${p => p.$disabled ? "none" : "auto"};

  &:hover {
    border-color: ${p => p.$disabled ? p.theme.colors.grey02 : `${p.theme.colors.blue}40`};
  }
`

export const SourceCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`

export const SourceCardIcon = styled.span<{ $color?: "blue" | "muted" }>`
  display: flex;
  color: ${p => p.$color === "blue" ? p.theme.colors.blue : p.theme.colors.text02};
`

export const SourceCardTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${p => p.theme.colors.darkBlack};
`

export const SourceUrlRow = styled.div`
  display: flex;
  gap: 10px;
`

export const SourceUrlInput = styled.input`
  flex: 1;
  box-sizing: border-box;
  padding: 10px 14px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-family: ${p => p.theme.fonts.body};
  color: ${p => p.theme.colors.darkBlack};
  background: ${p => p.theme.colors.grey01};
  outline: none;

  &:focus {
    box-shadow: 0 0 0 2px ${p => p.theme.colors.blue}30;
  }

  &::placeholder {
    color: ${p => p.theme.colors.text02};
    opacity: 0.6;
  }
`

export const SourceUrlAddButton = styled.button`
  padding: 10px 22px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  font-family: ${p => p.theme.fonts.body};
  color: ${p => p.theme.colors.white};
  background: ${p => p.theme.colors.blue};
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 2px 8px ${p => p.theme.colors.blue}18;

  &:hover:not(:disabled) {
    box-shadow: 0 4px 12px ${p => p.theme.colors.blue}28;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

export const DropZone = styled.div<{ $isDragging: boolean; $hasFile: boolean; $disabled?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  min-height: 120px;
  padding: 20px;
  border: 2px dashed ${p =>
    p.$disabled ? p.theme.colors.grey02
    : p.$isDragging ? p.theme.colors.blue
    : p.$hasFile ? p.theme.colors.green
    : p.theme.colors.grey02};
  border-radius: 12px;
  background: ${p =>
    p.$disabled ? p.theme.colors.white
    : p.$isDragging ? `${p.theme.colors.blue}08`
    : p.$hasFile ? "rgba(39, 174, 96, 0.04)"
    : p.theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${p => p.$disabled ? "default" : p.$hasFile ? "default" : "pointer"};
  transition: all 0.25s ease;
  opacity: ${p => p.$disabled ? 0.4 : 1};
  pointer-events: ${p => p.$disabled ? "none" : "auto"};

  &:hover {
    border-color: ${p =>
      p.$disabled ? p.theme.colors.grey02
      : p.$hasFile ? p.theme.colors.green
      : p.theme.colors.blue}66;
    background: ${p =>
      p.$disabled ? p.theme.colors.white
      : p.$hasFile ? "rgba(39, 174, 96, 0.04)"
      : `${p.theme.colors.blue}06`};
  }
`

export const DropZoneContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`

export const DropZoneIconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${p => p.theme.colors.blue}12;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.colors.blue};
  margin-bottom: 4px;
  transition: transform 0.2s ease;

  ${DropZone}:hover & {
    transform: scale(1.08);
  }
`

export const DropZoneTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${p => p.theme.colors.darkBlack};
`

export const DropZoneHint = styled.span`
  font-size: 13px;
  color: ${p => p.theme.colors.text02};
  margin-bottom: 8px;
`

export const ChooseFilesButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 18px;
  border: 1px solid ${p => p.theme.colors.blue}30;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  font-family: ${p => p.theme.fonts.body};
  color: ${p => p.theme.colors.blue};
  background: ${p => p.theme.colors.blue}08;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${p => p.theme.colors.blue}14;
  }

  &:active {
    transform: scale(0.97);
  }
`

export const SelectedFileInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`

export const SelectedFileName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.theme.colors.darkBlack};
  word-break: break-all;
  text-align: center;
`

export const SelectedFileSize = styled.span`
  font-size: 12px;
  color: ${p => p.theme.colors.text02};
`

export const ChangeFileLink = styled.button`
  appearance: none;
  background: none;
  border: none;
  color: ${p => p.theme.colors.blue};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  margin-top: 4px;

  &:hover {
    text-decoration: underline;
  }
`

export const FileErrorText = styled.div`
  margin-top: -12px;
  font-size: 13px;
  color: ${p => p.theme.colors.red};
`

export const SourceTextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-family: ${p => p.theme.fonts.body};
  line-height: 1.5;
  color: ${p => p.theme.colors.darkBlack};
  background: ${p => p.theme.colors.grey01};
  resize: none;
  outline: none;

  &:focus {
    box-shadow: 0 0 0 2px ${p => p.theme.colors.blue}30;
  }

  &::placeholder {
    color: ${p => p.theme.colors.text02};
    opacity: 0.6;
  }
`

export const TextCharCount = styled.div<{ $error?: boolean }>`
  font-size: 12px;
  color: ${p => p.$error ? p.theme.colors.red : p.theme.colors.text02};
  text-align: right;
  margin-top: 6px;
`

export const InstructionsStatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  min-height: 24px;
`

export const InstructionsStatusText = styled.span`
  font-size: 13px;
  line-height: 1.4;
  color: ${p => p.theme.colors.text02};
`

export const RecentInstructionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const RecentInstructionItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${p => p.theme.colors.grey01};
`

export const RecentInstructionText = styled.p`
  margin: 0;
  min-width: 0;
  font-size: 13px;
  line-height: 1.4;
  color: ${p => p.theme.colors.darkBlack};
  white-space: pre-wrap;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

export const SourceFooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

export const SourceFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 20px 40px 32px;
  border-top: 1px solid ${p => p.theme.colors.grey02}50;
`

// -- Profile Selection Step Styles --

export const ProfileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 40px 24px;
  max-height: 50vh;
  overflow-y: auto;
`

export const ProfileListItem = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 18px;
  border: 1.5px solid ${p => p.$isSelected ? p.theme.colors.blue : p.theme.colors.grey02};
  border-radius: 12px;
  background: ${p => p.$isSelected ? `${p.theme.colors.blue}06` : p.theme.colors.white};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${p => p.$isSelected ? p.theme.colors.blue : `${p.theme.colors.blue}50`};
    background: ${p => p.$isSelected ? `${p.theme.colors.blue}06` : `${p.theme.colors.blue}04`};
  }
`

export const ProfileRadio = styled.div<{ $isSelected: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${p => p.$isSelected ? p.theme.colors.blue : p.theme.colors.grey02};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
  transition: border-color 0.15s ease;
`

export const ProfileRadioDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${p => p.theme.colors.blue};
`

// The creation modal's channel row is one card: the clickable channel, its category summary and
// the open drawer all sit inside the same selected border, so the blue state covers the whole area.
export const CreationChannelCard = styled.div<{ $isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  flex: none; // the list is a capped flex column; without this an open drawer gets squeezed instead of scrolling
  border: 1.5px solid ${p => p.$isSelected ? p.theme.colors.blue : p.theme.colors.grey02};
  border-radius: 12px;
  background: ${p => p.$isSelected ? `${p.theme.colors.blue}06` : p.theme.colors.white};
  transition: all 0.15s ease;

  &:hover {
    border-color: ${p => p.$isSelected ? p.theme.colors.blue : `${p.theme.colors.blue}50`};
    background: ${p => p.$isSelected ? `${p.theme.colors.blue}06` : `${p.theme.colors.blue}04`};
  }
`

export const CreationChannelRow = styled.div`
  display: flex;
  align-items: center;

  > :first-child {
    flex: 1;
    min-width: 0;
  }

  > button:last-child {
    padding-right: 18px;
  }
`

export const CreationChannelItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 18px;
  cursor: pointer;

  > input[type="checkbox"] {
    margin-top: 5px; // centres the 16px box against the 26px avatar beside it
  }
`

export const ProfileListItemContent = styled.div`
  flex: 1;
  min-width: 0;
`

export const ProfileListItemName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.theme.colors.darkBlack};
  line-height: 1.4;
`

export const ProfileListItemDetail = styled.div`
  font-size: 13px;
  color: ${p => p.theme.colors.text02};
  line-height: 1.4;
  margin-top: 2px;
`

// Theme-row swatch: a single circle whose upper-left region is the theme
// background color and lower-right corner is one accent color (or gradient).
// The accent layer is clip-pathed so it works with both plain hex and CSS
// gradients. The DIAG_CUT_PCT is the offset of the diagonal cut from each
// edge — accent occupies ~1/3 of the circle, background ~2/3.
export const ThemeSwatchRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`

export const ThemeSwatchCircle = styled.span`
  position: relative;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15);
`

export const ThemeSwatchBgLayer = styled.span`
  position: absolute;
  inset: 0;
`

export const ThemeSwatchAccentLayer = styled.span`
  position: absolute;
  inset: 0;
  clip-path: polygon(20% 100%, 100% 100%, 100% 20%);
`

export const BrandCarousel = styled.div`
  display: flex;
  gap: 12px;
  padding-bottom: 4px;
  overflow-x: auto;
`

// Brand carousel slide dimensions mirror production canvas (1080x1350, 4:5
// portrait); typography is scaled down ~5x for the preview while preserving
// the visual hierarchy.
export const BrandSlide = styled.div`
  width: 240px;
  height: 300px;
  flex-shrink: 0;
  border-radius: 10px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
  position: relative;
`

export const BrandSlideTop = styled.div`
  position: absolute;
  top: 14px;
  left: 18px;
  right: 18px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  pointer-events: none;
`

export const BrandSlideLogo = styled.div`
  flex-shrink: 0;
  /* Concrete width/height are set inline per logo by QuoteSlide
     (computeLogoSize). The inline SVG inside fills this box; explicit %
     dimensions let viewBox-only SVGs render at the wrapper's size. */
  & svg {
    width: 100%;
    height: 100%;
  }
`

export const BrandSlideLogoImg = styled.img`
  flex-shrink: 0;
  /* Concrete width/height are set inline per logo by QuoteSlide
     (computeLogoSize), mirroring production's logo-sizing formula so the
     preview matches what the real slide will render. Object-fit keeps the
     aspect ratio inside that explicit box. */
  object-fit: contain;
  object-position: left center;
`

export const BrandSlideContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 0;
  padding-top: 30px;
`

export const BrandSlideDivider = styled.div`
  width: 36px;
  height: 3px;
  border-radius: 2px;
  margin: 14px 0;
`

export const BrandSlideSubline = styled.div`
  font-size: 11px;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

export const BrandSlideQuote = styled.div`
  font-size: 22px;
  font-weight: 800;
  line-height: 1.18;
  letter-spacing: -0.02em;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

// -- GradientPicker Styles --

export const GradientPopover = styled.div`
  position: fixed;
  z-index: 1000;
  background: ${p => p.theme.colors.white};
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.06);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

// Centred block holding the narrow bar with swatches just below it, fully
// inside the bar's horizontal extent. Block height = bar + gap + swatch.
export const GradientBarBlock = styled.div`
  position: relative;
  width: 80px;
  height: 46px; /* 18 bar + 4 gap + 24 swatch */
  margin: 0 auto;
`

export const GradientBar = styled.div`
  width: 100%;
  height: 18px;
  border-radius: 999px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.08);
`

export const GradientStopSwatch = styled.button`
  position: absolute;
  top: 22px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  padding: 0;
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.35), 0 2px 6px rgba(0, 0, 0, 0.2);
  cursor: pointer;
`

// -- ColorPickerPopover Styles --

// Invisible layer covering the whole screen under the picker. It eats every
// click, so nothing behind the picker can be used until the user finishes
// with Save, Cancel, or Escape. 
// This stops two things: losing an edit by clicking somewhere by accident,
// and walking off to another screen while the picker is still open on top of it.
export const ColorPickerBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999;
`

const pickerShake = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-7px); }
  40% { transform: translateX(6px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(3px); }
`

// $shake: blocked backdrop clicks nudge the picker instead of silently doing
// nothing, the wiggle tells the user "finish this edit first".
export const ColorPickerBox = styled.div<{ $shake?: boolean }>`
  ${p =>
    p.$shake &&
    css`
      animation: ${pickerShake} 0.35s ease-in-out;
      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}
  position: fixed;
  z-index: 1000;
  background: ${p => p.theme.colors.white};
  border-radius: 14px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.06);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  /* Restyle react-colorful's internals to match the polished look. The
     library's CSS is loaded with low specificity, so plain selectors win. */
  .react-colorful {
    width: 100%;
    height: auto;
    gap: 14px;
  }
  .react-colorful__saturation {
    border-radius: 10px;
    height: 126px;
    border-bottom: none;
  }
  .react-colorful__hue,
  .react-colorful__alpha {
    height: 18px;
    border-radius: 999px;
  }
  .react-colorful__pointer {
    width: 22px;
    height: 22px;
    border: 2px solid #ffffff;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.4), 0 2px 6px rgba(0, 0, 0, 0.25);
  }
  .react-colorful__hue-pointer,
  .react-colorful__alpha-pointer {
    width: 18px;
    height: 18px;
    border: 2px solid #ffffff;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.35), 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`

export const ColorPickerToolbar = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
`

export const ColorPickerFooter = styled.div`
  display: flex;
  gap: 8px;

  button {
    flex: 1;
  }
`

export const ColorPickerInputCluster = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 10px;
  border: 1px solid ${p => p.theme.colors.grey02};
  border-radius: 10px;
  background: ${p => p.theme.colors.white};
  min-width: 0;
`

export const ColorPickerSwatch = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.18);
  margin-right: 10px;
`

export const ColorPickerHashPrefix = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
  font-weight: 500;
  color: ${p => p.theme.colors.darkBlack};
  margin-right: 2px;
  user-select: none;
`

export const ColorPickerHexInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
  font-weight: 500;
  color: ${p => p.theme.colors.darkBlack};
  text-transform: uppercase;
  letter-spacing: 0.02em;
  min-width: 0;
  padding: 0;
`

export const ColorPickerClusterDivider = styled.span`
  width: 1px;
  align-self: stretch;
  margin: 6px 10px;
  background: ${p => p.theme.colors.grey02};
`

export const ColorPickerAlphaGroup = styled.div`
  display: flex;
  align-items: baseline;
  gap: 2px;
`

export const ColorPickerAlphaInput = styled.input`
  width: 32px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  text-align: right;
  color: ${p => p.theme.colors.darkBlack};
  padding: 0;

  /* Hide native number spinners — they clutter the look. */
  appearance: textfield;
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`

export const ColorPickerPercentLabel = styled.span`
  font-size: 13px;
  color: ${p => p.theme.colors.text02};
`

export const ColorPickerEyedropperButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid ${p => p.theme.colors.grey02};
  background: ${p => p.theme.colors.white};
  border-radius: 10px;
  cursor: pointer;
  color: ${p => p.theme.colors.text02};
  padding: 0;
  flex-shrink: 0;

  &:hover {
    background: ${p => p.theme.colors.grey01};
    color: ${p => p.theme.colors.darkBlack};
  }
`

// -- ThemeRow Styles --

// Click affordance: a generous hit area + a dashed halo on hover so the user
// can see what they're about to select even when the visual target (e.g. a 3px
// divider) is tiny. The neutral gray works on both light and dark slides.
export const ClickHalo = styled.div`
  cursor: pointer;
  border-radius: 4px;
  outline: 2px dashed transparent;
  outline-offset: 2px;
  transition: outline-color 120ms ease;
  width: fit-content;

  &:hover {
    outline-color: rgba(127, 127, 127, 0.75);
  }
`

// Inline variant for the highlight word inside the flowing quote text.
// `inline-block` keeps the outline painted as a single rectangle (a plain
// inline span breaks the outline across line-box edges).
export const ClickHaloInline = styled.span`
  display: inline-block;
  cursor: pointer;
  padding: 2px 6px;
  margin: 0 -6px;
  border-radius: 4px;
  outline: 2px dashed transparent;
  outline-offset: 1px;
  transition: outline-color 120ms ease;

  &:hover {
    outline-color: rgba(127, 127, 127, 0.75);
  }

  /* The outer ClickHalo (wrapping the whole headline) also matches :hover
     via ancestor bubbling. Suppress its outline so only this inner halo
     reads as the active target. */
  ${ClickHalo}:has(&:hover) {
    outline-color: transparent;
  }
`

// Small ✕ button anchored to the top-right of a slide. Hidden by default,
// revealed when the parent slide is hovered. Outline + transparent fill in
// the contrast colour of the slide background — black on light, white on dark.
export const PreviewDeleteButton = styled.button<{ $dark: boolean }>`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 22px;
  height: 22px;
  border: 1.5px solid ${p => p.$dark ? "#ffffff" : "#000000"};
  border-radius: 50%;
  background: transparent;
  color: ${p => p.$dark ? "#ffffff" : "#000000"};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  opacity: 0;
  transition: opacity 120ms ease;
  z-index: 1;
`

export const SlideWithDelete = styled.div`
  position: relative;
  flex-shrink: 0;

  &:hover ${PreviewDeleteButton} {
    opacity: 1;
  }
`

export const PictureUploadingSlide = styled.div`
  width: 280px;
  height: 336px;
  flex-shrink: 0;
  border-radius: 10px;
  background: ${p => p.theme.colors.grey02};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
`

export const PictureUploadingLabel = styled.div`
  font-size: 13px;
  color: ${p => p.theme.colors.text02};
`

// A picture's corner can be any brightness, so the × gets its own scrim.
export const PictureDeleteButton = styled(PreviewDeleteButton).attrs({ $dark: true })`
  background: rgba(0, 0, 0, 0.55);
  border-color: transparent;
`

// Same outer footprint as BrandSlide (280x336).
export const PictureSlide = styled.img`
  width: 280px;
  height: 336px;
  flex-shrink: 0;
  display: block;
  object-fit: cover;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
`

export const PictureOriginBadge = styled.span`
  position: absolute;
  left: 8px;
  bottom: 8px;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #ffffff;
`

// Dashed "+" tile that adds a new accent colour. Height matches BrandSlide's
// outer footprint (240x300 content + 18x20 padding under default content-box
// sizing → 280x336 outer); width is narrower since the tile is just an
// affordance, not a full preview. border-box keeps the dashed border inside
// these dimensions.
// Full-width drop target: a lone "+" tile read as a placeholder when there were no pictures.
export const PicturesEmptyState = styled.div<{ $isDragging: boolean }>`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border: 1px dashed ${p => p.$isDragging ? p.theme.colors.blue : p.theme.colors.grey02};
  border-radius: 12px;
  background: ${p => p.$isDragging ? `${p.theme.colors.blue}08` : p.theme.colors.grey01};
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease;

  &:hover {
    border-color: ${p => p.theme.colors.blue}66;
    background: ${p => p.theme.colors.blue}06;
  }
`

export const PicturesDropZone = styled.div<{ $isDragging: boolean }>`
  position: relative;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border: 1px dashed ${p => p.theme.colors.blue};
    border-radius: 10px;
    background: ${p => p.theme.colors.blue}08;
    pointer-events: none;
    opacity: ${p => (p.$isDragging ? 1 : 0)};
    transition: opacity 120ms ease;
  }
`

export const PicturesEmptyIcon = styled(DropZoneIconCircle)`
  width: 40px;
  height: 40px;
  margin-bottom: 0;
  flex-shrink: 0;

  ${PicturesEmptyState}:hover & {
    transform: scale(1.08);
  }
`

export const PicturesEmptyText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`

export const PicturesEmptyTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.theme.colors.darkBlack};
`

export const PicturesEmptyActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
`

export const PicturesEmptyHint = styled.span`
  font-size: 13px;
  line-height: 1.45;
  color: ${p => p.theme.colors.text02};
`

export const AddSlide = styled.button`
  box-sizing: border-box;
  width: 93px;
  height: 336px;
  flex-shrink: 0;
  border: 1px solid ${p => p.theme.colors.grey02};
  border-radius: 10px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${p => p.theme.colors.text02};
  padding: 0;
  transition: border-color 120ms ease, background 120ms ease, color 120ms ease;

  &:hover {
    border-color: transparent;
    background: ${p => p.theme.colors.primarySubtle};
    color: ${p => p.theme.colors.blue};
  }
`

export const PicturePickerAddTile = styled(AddSlide)`
  width: 40px;
  height: ${PICTURE_PICKER_TILE_HEIGHT};
  border-radius: 8px;
  scroll-snap-align: start;

  &:disabled {
    cursor: progress;
  }
`

export const PicturePickerUploadingThumb = styled(PictureUploadingSlide)`
  width: 120px;
  height: ${PICTURE_PICKER_TILE_HEIGHT};
  border-radius: 8px;
  gap: 8px;
  scroll-snap-align: start;
`

export const PicturePickerUploadingLabel = styled(PictureUploadingLabel)`
  padding: 0 8px;
  font-size: 11px;
  text-align: center;
`

// Circular paging arrow overlaid on a visual preview (StaticVisual carousels). `$side` is a
// transient prop (not forwarded to the DOM) and selects which edge it pins to.
export const SlideArrow = styled.button<{ $side: "left" | "right" }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === "left" ? "left: 8px;" : "right: 8px;")}
  transform: translateY(-50%);
  z-index: 2;
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: rgba(15, 23, 42, 0.45);
  color: #fff;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
`

// -- Inline rename (ProfileCard + BatchBox headers) --

// The name itself is the control: plain text at rest, a pill styled like CountChip on hover.
// Padding is cancelled by the margins so the pill appears without moving anything.
export const RenameTrigger = styled.button`
  font: inherit;
  letter-spacing: inherit;
  color: inherit;
  text-align: inherit;
  background: transparent;
  border: 0;
  border-radius: 999px;
  padding: 2px 8px;
  margin: -2px -8px;
  min-width: 0;
  cursor: text;
  transition: color 0.14s ease, background 0.14s ease;

  &:hover {
    background: ${p => p.theme.colors.primarySubtle};
  }

  &:hover,
  &:hover ${BatchSourceName} {
    color: ${p => p.theme.colors.blue};
  }

  // In running text a negative right margin would pull the separator into the pill.
  ${BatchMeta} & {
    margin-right: 0;
  }

  ${BatchMeta} &:hover {
    font-weight: 600;
  }

  &:focus-visible {
    outline: 2px solid ${p => p.theme.colors.blue};
    outline-offset: 1px;
  }
`

// The editor sets its own typography so it looks the same inside the bold profile name
// and inside the small grey batch meta line.
export const RenameEditor = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  vertical-align: middle;
`

export const RenameInput = styled.input`
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: -0.01em;
  color: ${p => p.theme.colors.darkBlack};
  background: ${p => p.theme.colors.white};
  border: 1.5px solid ${p => p.theme.colors.blue};
  border-radius: 6px;
  padding: 2px 8px;
  min-width: 180px;
  max-width: 320px;
  outline: none;
`

export const RenameHint = styled.span`
  font-size: 11.5px;
  font-weight: 400;
  letter-spacing: 0;
  color: ${p => p.theme.colors.text03};
  white-space: nowrap;
`

export const PlanningControlBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const PlanningSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px 16px;
  border-radius: 12px;
  background: color-mix(in srgb, ${p => p.theme.colors.blue} 5%, ${p => p.theme.colors.white});
`

export const PlanningDivider = styled.hr`
  margin: 2px 0;
  border: 0;
  border-top: 1px solid ${p => p.theme.colors.grey02};
`

export const PlanningLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${p => p.theme.colors.blue};
`

export const PlanningDayChips = styled.div`
  display: flex;
  justify-content: space-between;
`

export const PlanningDayChip = styled.button<{ $active?: boolean }>`
  width: 38px;
  height: 38px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid ${p => (p.$active ? p.theme.colors.primaryLight : p.theme.colors.grey02)};
  background: ${p => (p.$active ? p.theme.colors.primarySubtle : p.theme.colors.white)};
  color: ${p => (p.$active ? p.theme.colors.blue : p.theme.colors.text02)};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.14s ease, color 0.14s ease;

  &:hover:not(:disabled) {
    border-color: ${p => p.theme.colors.primaryLight};
    background: ${p => p.theme.colors.primarySubtle};
    color: ${p => p.theme.colors.blue};
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`

export const PlanningWeekTrigger = styled.button<{ $open?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 42px;
  padding: 0 12px 0 14px;
  border: 1px solid ${p => (p.$open ? p.theme.colors.blue : p.theme.colors.grey02)};
  border-radius: 10px;
  background: ${p => p.theme.colors.white};
  color: ${p => p.theme.colors.text02};
  text-align: left;
  cursor: pointer;

  svg {
    color: ${p => p.theme.colors.blue};
  }

  &:hover:not(:disabled) {
    border-color: ${p => p.theme.colors.blue};
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`

export const PlanningWeekMenuList = styled.div`
  max-height: 288px;
  overflow-y: auto;
`

export const PlanningWeekTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.theme.colors.text01};
`

export const PlanningWeekMeta = styled.span`
  flex: 1;
  font-size: 12px;
  font-weight: 500;
  color: ${p => p.theme.colors.blue};
`

export const PlanningUpgradeNotice = styled.p`
  margin: 0;
  font-size: 12.5px;
  line-height: 1.45;
  color: ${p => p.theme.colors.text02};
`

export const PlanningUpgradeLink = styled.a`
  font-weight: 600;
  color: ${p => p.theme.colors.blue};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

export const PlanningActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

export const PlanningPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: none;
  border: 1px solid ${p => p.theme.colors.grey02};
  border-radius: 999px;
  height: 36px;
  padding: 0 14px;
  background: ${p => p.theme.colors.white};
  color: ${p => p.theme.colors.text01};
  font-size: 13.5px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: ${p => p.theme.colors.grey01};
  }
`

export const PlanningIconButton = styled.button<{ $locked?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 36px;
  height: 36px;
  border: 1px ${p => (p.$locked ? "dashed" : "solid")} ${p => p.theme.colors.grey02};
  border-radius: 50%;
  background: ${p => p.theme.colors.white};
  color: ${p => (p.$locked ? p.theme.colors.text03 : p.theme.colors.text02)};
  cursor: pointer;

  &:hover {
    background: ${p => p.theme.colors.grey01};
    color: ${p => p.theme.colors.text01};
  }
`

export const PlanningPopoverBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 8px 16px; // MenuContainer already pads 8px top and bottom
`

export const PlanningPopoverNote = styled.p`
  margin: 0;
  padding: 8px 12px;
  border-radius: 8px;
  background: ${p => p.theme.colors.primarySubtle};
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  color: ${p => p.theme.colors.blue};
`

export const InstructionsTextArea = styled(SourceTextArea)`
  border: 1px solid ${p => p.theme.colors.grey08};
`

export const RecentInstructionsCard = styled(SourceCard)`
  border: 1px solid ${p => p.theme.colors.grey08};
`

export const RecentUseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: 28px;
  padding: 0 14px;
  border: none;
  border-radius: 48px;
  background: ${p => p.theme.colors.blue};
  color: ${p => p.theme.colors.white};
  font-family: ${p => p.theme.fonts.body};
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`
